import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FiCheck, FiChevronRight, FiFileText, FiInbox, FiMail, FiTrash2 } from "react-icons/fi"
import AppLayout from "../layout/AppLayout"
import ActionButton from "../components/applications/ActionButton"
import { announceMessagesChanged } from "../hooks/useUnreadMessages"
import {
  deleteMessage,
  getMessages,
  InboxMessage,
  markAllMessagesRead,
  markMessageRead
} from "../services/messageService"
import { formatApiError } from "../utils/formatApiError"
import {
  applicationTypeLabel,
  groupByDay,
  messageKind,
  messageReference,
  timeLabel
} from "../utils/messageDetails"

type Filter = "all" | "unread"

function MessageIcon({ message }: { message: InboxMessage }) {
  const kind = messageKind(message)
  const tone = kind === "filed" ? "bg-[#DCFCE7] text-[#166534]" : "bg-appAvatar text-dantzBlue"

  return (
    <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${tone}`}>
      {kind === "filed" ? (
        <FiCheck size={20} aria-hidden="true" />
      ) : kind === "received" ? (
        <FiInbox size={20} aria-hidden="true" />
      ) : kind === "power-of-attorney" ? (
        <FiFileText size={20} aria-hidden="true" />
      ) : (
        <FiMail size={20} aria-hidden="true" />
      )}
    </span>
  )
}

export default function MessagesPage() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<InboxMessage[] | null>(null)
  const [error, setError] = useState("")
  const [filter, setFilter] = useState<Filter>("all")
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await getMessages()
      setMessages(result?.messages ?? [])
    } catch (loadError) {
      setError(formatApiError(loadError, "The messages could not be loaded."))
      setMessages([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function open(message: InboxMessage) {
    if (!message.read) {
      setMessages((current) =>
        (current ?? []).map((entry) => (entry.id === message.id ? { ...entry, read: true } : entry))
      )
      try {
        await markMessageRead(message.id)
        announceMessagesChanged()
      } catch {
        // Reading is not blocked by a failed mark
      }
    }

    if (message.link) navigate(message.link)
  }

  async function readAll() {
    try {
      await markAllMessagesRead()
      setMessages((current) => (current ?? []).map((entry) => ({ ...entry, read: true })))
      announceMessagesChanged()
    } catch (readError) {
      setError(formatApiError(readError, "The messages could not be marked as read."))
    }
  }

  async function remove(message: InboxMessage) {
    if (deletingId) return
    setDeletingId(message.id)
    setError("")
    try {
      await deleteMessage(message.id)
      setMessages((current) => (current ?? []).filter((entry) => entry.id !== message.id))
      announceMessagesChanged()
    } catch (deleteError) {
      setError(formatApiError(deleteError, "The message could not be deleted. Please try again."))
    } finally {
      setDeletingId(null)
    }
  }

  const all = messages ?? []
  const unread = all.filter((message) => !message.read).length
  const shown = filter === "unread" ? all.filter((message) => !message.read) : all
  const tabs: Array<{ value: Filter; label: string; count: number }> = [
    { value: "all", label: "All", count: all.length },
    { value: "unread", label: "Unread", count: unread }
  ]

  return (
    <AppLayout crumb="Messages" contentWidth="max-w-[960px]">
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-1.5">
          <h1 className="font-heading text-[30px] font-semibold tracking-[-0.01em] text-dantzBlue">Messages</h1>
          <p className="text-sm text-[#4A5565]">Updates about your applications.</p>
        </header>

        {error && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {messages === null ? (
          <p className="text-sm text-secondary4">Loading messages...</p>
        ) : (
          <section className="flex flex-col overflow-hidden rounded-2xl border border-appLine bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#EEF1F4] px-5 py-3.5">
              <div role="group" aria-label="Filter messages" className="flex gap-1 rounded-[10px] bg-[#F1F3F6] p-1">
                {tabs.map((tab) => {
                  const active = filter === tab.value

                  return (
                    <button
                      key={tab.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setFilter(tab.value)}
                      className={`flex h-9 items-center gap-2 rounded-lg px-3.5 text-[13px] ${
                        active
                          ? "bg-white font-semibold text-dantzBlue shadow-[0_1px_2px_rgba(15,30,50,0.12)]"
                          : "font-medium text-[#4A5565] hover:text-dantzBlue"
                      }`}
                    >
                      {tab.label}
                      <span className="text-xs text-appMuted">{tab.count}</span>
                    </button>
                  )
                })}
              </div>

              {unread > 0 && (
                <button
                  type="button"
                  onClick={readAll}
                  className="flex h-10 items-center gap-2 rounded-[10px] border border-[#C8D1DB] bg-white px-3.5 text-[13px] font-semibold text-dantzBlue transition-colors hover:bg-appBg"
                >
                  <FiCheck size={16} aria-hidden="true" />
                  Mark all as read
                </button>
              )}
            </div>

            {shown.length === 0 ? (
              <div className="flex flex-col items-center gap-2.5 px-6 py-14 text-center">
                <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#DCFCE7] text-[#166534]">
                  <FiCheck size={24} aria-hidden="true" />
                </span>
                <p className="text-base font-semibold text-appInk">You're all caught up</p>
                <p className="text-sm text-[#4A5565]">New messages about your applications will appear here.</p>
              </div>
            ) : (
              groupByDay(shown).map((group) => (
                <div key={group.label} className="flex flex-col">
                  <h2 className="border-b border-[#EEF1F4] bg-[#FAFBFC] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.06em] text-appMuted">
                    {group.label}
                  </h2>

                  {group.items.map((message) => {
                    const type = applicationTypeLabel(message)
                    const reference = messageReference(message)
                    // A read message without a page has nothing left to open
                    const opens = Boolean(message.link) || !message.read

                    return (
                      <article
                        key={message.id}
                        className={`group relative flex flex-wrap items-start gap-x-4 gap-y-3 border-b border-[#EEF1F4] px-5 py-[18px] transition-colors hover:bg-[#EEF3F8] focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-dantzBlue ${
                          message.read ? "bg-white" : "bg-[#F6F9FC]"
                        }`}
                      >
                        <MessageIcon message={message} />

                        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1.5">
                          <h3 className={`text-[15px] ${message.read ? "font-medium text-appInk" : "font-bold text-dantzBlue"}`}>
                            {opens ? (
                              <button
                                type="button"
                                onClick={() => open(message)}
                                className="text-left outline-none after:absolute after:inset-0 after:content-['']"
                              >
                                {message.title}
                              </button>
                            ) : (
                              message.title
                            )}
                          </h3>

                          {message.body && <p className="text-sm leading-normal text-[#4A5565]">{message.body}</p>}

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {reference && (
                              <span className="inline-flex w-fit rounded-md bg-[#F1F3F6] px-2.5 py-[3px] text-xs font-semibold text-gray-700">
                                {type ? `${type} · ${reference}` : reference}
                              </span>
                            )}
                            <span className="whitespace-nowrap text-[13px] text-appMuted">{timeLabel(message.createdAt)}</span>
                          </div>
                        </div>

                        <div className="ml-auto flex items-center gap-2.5 self-center">
                          <ActionButton
                            label={`Delete message: ${message.title}`}
                            title="Delete"
                            icon={<FiTrash2 size={16} />}
                            disabled={deletingId !== null}
                            onClick={() => remove(message)}
                            className="relative z-10 border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40"
                          />
                          <FiChevronRight
                            size={20}
                            aria-hidden="true"
                            className="text-[#8A94A3] transition group-hover:translate-x-[3px] group-hover:text-dantzBlue"
                          />
                        </div>
                      </article>
                    )
                  })}
                </div>
              ))
            )}
          </section>
        )}
      </div>
    </AppLayout>
  )
}
